import { useCallback } from 'react';

import { useAppDispatch } from '@/store';
import { deleteFileThunk } from '@/store/thunks/sixDetail/deleteFileThunk';
import type { FileEntry } from '@/types/SixDetail.types';
import type { SaveableFile } from '@/utils/saveFilesToDirectory.utils';
import { saveFilesToDirectory } from '@/utils/saveFilesToDirectory.utils';
import { getDownloadedSixDetailFilePayload } from '@/utils/download.utils';
import { showErrorToast, showSuccessToast } from '@/utils/toast.utils';

interface BulkActionsArgs {
  sixId: string;
  visibleFiles: FileEntry[];
  selectedFileIds: string[];
  currentUserId: string | undefined;
}

export function useSixDetailBulkActions({
  sixId,
  visibleFiles,
  selectedFileIds,
  currentUserId,
}: BulkActionsArgs) {
  const dispatch = useAppDispatch();

  const handleBulkDownload = useCallback((): void => {
    if (typeof sixId !== 'string' || sixId.trim().length === 0) return;
    const filesInView = visibleFiles.filter((item) => item.isNavigationEntry !== true);
    const filesToDownload = filesInView.filter(
      (item) => selectedFileIds.includes(item.id) && item.kind === 'file',
    );
    if (filesToDownload.length === 0) return;

    // saveFilesToDirectory must be called synchronously within this click
    // handler so the directory picker keeps its user gesture. File payloads
    // are fetched lazily through loadDownloadedFiles, after the picker opens.
    const downloadedFiles: SaveableFile[] = [];
    const loadDownloadedFiles = async(): Promise<SaveableFile[]> => {
      for (const item of filesToDownload) {
        const filePayload = await getDownloadedSixDetailFilePayload({
          item,
          currentUserId,
          dispatch,
          sixId,
        });
        if (filePayload !== undefined) {
          downloadedFiles.push(filePayload);
        }
      }
      return downloadedFiles;
    };

    void saveFilesToDirectory(loadDownloadedFiles).then((saveResult) => {
      if (saveResult === 'saved') {
        showSuccessToast(
          downloadedFiles.length === 1
            ? 'File downloaded successfully'
            : `${downloadedFiles.length} files downloaded successfully`,
        );
        return;
      }

      if (saveResult === 'unsupported') {
        showErrorToast('Bulk download requires a browser that supports folder selection');
      }
    });
  }, [currentUserId, dispatch, selectedFileIds, sixId, visibleFiles]);

  const handleBulkDelete = useCallback((): void => {
    if (typeof sixId !== 'string' || sixId.trim().length === 0) return;

    const filesInView = visibleFiles.filter((item) => item.isNavigationEntry !== true);
    const fileIds = filesInView
      .filter((item) => selectedFileIds.includes(item.id) && item.kind === 'file')
      .map((item) => item.id);

    if (fileIds.length === 0) return;

    const deleteSequentially = async(): Promise<void> => {
      for (const fileId of fileIds) {
        await dispatch(deleteFileThunk({ sixId, fileId })).unwrap();
      }
    };

    void deleteSequentially()
      .then(() => {
        showSuccessToast(
          fileIds.length === 1
            ? 'File deleted successfully'
            : `${fileIds.length} files deleted successfully`,
        );
      })
      .catch(() => {
        showErrorToast('Failed to delete file');
      });
  }, [dispatch, selectedFileIds, sixId, visibleFiles]);

  return { handleBulkDownload, handleBulkDelete };
}
